// ============================================================================
// ROLE PERMISSION SERVICE
// ============================================================================
// Calls /api/v1/RolePermissions and /by-roles. The /by-roles endpoint is the
// hot path: invoked at login time by AuthContext to drive the permission
// engine + sidebar filtering.

import { get, post, put, del } from '../client/axiosClient';
import { rolePermissionEndpoints } from '../endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  PaginatedResponse,
  EmptyResponse,
  ID,
  RolePermission,
  RolePermissionMerged,
  CreateRolePermissionRequest,
  UpdateRolePermissionRequest,
  RolePermissionSearchRequest,
  KnownRole,
} from '../types';

class RolePermissionService {
  /**
   * GET /api/v1/RolePermissions (paginated, system-only).
   * Mirrors propertyService.getAll: transforms backend `PagedResult<T>`
   * into the frontend `PaginatedResponse<T>` shape.
   */
  async getAll(params?: RolePermissionSearchRequest): Promise<PaginatedResponse<RolePermission>> {
    const endpoint = params
      ? buildEndpointWithQuery('/RolePermissions', params)
      : rolePermissionEndpoints.list();

    const response = await get<any>(endpoint);
    const raw = response.data;
    const pagedResult = raw.data ?? {};

    return {
      success: raw.success,
      message: raw.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  /**
   * GET /api/v1/RolePermissions/:id
   */
  async getById(id: ID): Promise<ApiResponse<RolePermission>> {
    const response = await get<ApiResponse<RolePermission>>(rolePermissionEndpoints.getById(id));
    return response.data;
  }

  /**
   * GET /api/v1/RolePermissions/known-roles
   *
   * Public endpoint returning the DB-driven role catalog ordered by priority
   * ASC (highest first). Consumed at app boot to hydrate ROLE_PRIORITY so
   * adding a new role via the admin UI does not require a code deploy.
   */
  async getKnownRoles(): Promise<ApiResponse<KnownRole[]>> {
    const response = await get<ApiResponse<KnownRole[]>>(rolePermissionEndpoints.knownRoles());
    return response.data;
  }

  /**
   * GET /api/v1/RolePermissions/by-roles?roles=role1,role2
   *
   * Public endpoint. Returns the merged visible_modules + module_actions
   * across the supplied role codes. Roles that do not match any seeded row
   * are silently skipped (compare `sourceRoles` to the requested array to
   * detect missing roles).
   */
  async getByRoles(roles: ReadonlyArray<string>): Promise<ApiResponse<RolePermissionMerged>> {
    const response = await get<ApiResponse<RolePermissionMerged>>(
      rolePermissionEndpoints.byRoles(roles),
    );
    return response.data;
  }

  /**
   * POST /api/v1/RolePermissions (system-only)
   */
  async create(data: CreateRolePermissionRequest): Promise<ApiResponse<RolePermission>> {
    const response = await post<ApiResponse<RolePermission>>(
      rolePermissionEndpoints.create(),
      data,
    );
    return response.data;
  }

  /**
   * PUT /api/v1/RolePermissions/:id (system-only)
   */
  async update(id: ID, data: UpdateRolePermissionRequest): Promise<ApiResponse<RolePermission>> {
    const response = await put<ApiResponse<RolePermission>>(
      rolePermissionEndpoints.update(id),
      data,
    );
    return response.data;
  }

  /**
   * DELETE /api/v1/RolePermissions/:id?rowVersion=N (system-only).
   * System roles cannot be deleted.
   */
  async delete(id: ID, rowVersion: number): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(rolePermissionEndpoints.delete(id, rowVersion));
    return response.data;
  }
}

export const rolePermissionService = new RolePermissionService();
export default rolePermissionService;
